/**
 * Archive Trigger Form Component
 *
 * Form for selecting a month to archive with dry-run option.
 */

import { useState } from 'react';
import { Archive, AlertCircle, Loader2, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useArchivePreview } from '@/hooks/use-archive-management';

interface ArchiveTriggerFormProps {
  onTrigger: (year: number, month: number, dryRun: boolean) => void;
  isTriggering: boolean;
  retentionDays: number;
}

/**
 * Generate array of years for selection (past 5 years)
 */
function getYearOptions(): number[] {
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let i = 0; i < 5; i++) {
    years.push(currentYear - i);
  }
  return years;
}

/**
 * Get month options
 */
function getMonthOptions(): { value: number; label: string }[] {
  return [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];
}

/**
 * Get default month/year (previous month)
 */
function getDefaultMonthYear(): { year: number; month: number } {
  const now = new Date();
  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1);
  return {
    year: prevMonth.getFullYear(),
    month: prevMonth.getMonth() + 1,
  };
}

/**
 * Archive Trigger Form
 * Allows selecting month/year and triggering archive with dry-run option
 */
export function ArchiveTriggerForm({
  onTrigger,
  isTriggering,
  retentionDays,
}: ArchiveTriggerFormProps) {
  // Initialize with previous month as default
  const defaultValues = getDefaultMonthYear();
  const [selectedYear, setSelectedYear] = useState<number | null>(defaultValues.year);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(defaultValues.month);
  const [dryRun, setDryRun] = useState(true);

  // Get archive preview when month is selected
  const { data: preview, isLoading: isLoadingPreview } = useArchivePreview(
    selectedYear,
    selectedMonth
  );

  const handleSubmit = () => {
    if (selectedYear && selectedMonth) {
      onTrigger(selectedYear, selectedMonth, dryRun);
    }
  };

  const canSubmit =
    selectedYear !== null &&
    selectedMonth !== null &&
    !isTriggering &&
    !isLoadingPreview &&
    preview?.canArchive;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Archive className="h-5 w-5" />
          Trigger Archive
        </CardTitle>
        <CardDescription>
          Manually archive audit logs for a specific month. Logs older than {retentionDays} days
          will be archived to S3 and removed from the database.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Year/Month Selection */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="archive-year">Year</Label>
            <Select
              value={selectedYear?.toString() ?? ''}
              onValueChange={value => setSelectedYear(parseInt(value))}
            >
              <SelectTrigger id="archive-year">
                <SelectValue placeholder="Select year" />
              </SelectTrigger>
              <SelectContent>
                {getYearOptions().map(year => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="archive-month">Month</Label>
            <Select
              value={selectedMonth?.toString() ?? ''}
              onValueChange={value => setSelectedMonth(parseInt(value))}
            >
              <SelectTrigger id="archive-month">
                <SelectValue placeholder="Select month" />
              </SelectTrigger>
              <SelectContent>
                {getMonthOptions().map(month => (
                  <SelectItem key={month.value} value={month.value.toString()}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Preview Info */}
        {selectedYear && selectedMonth && (
          <div className="rounded-md border p-4 bg-muted/50">
            {isLoadingPreview ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading preview...
              </div>
            ) : preview ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Estimated records:</span>
                  <span className="font-medium">
                    {preview.estimatedRecordCount.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Archive exists:</span>
                  <span className="font-medium">{preview.archiveExists ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Can archive:</span>
                  <span
                    className={`font-medium ${preview.canArchive ? 'text-green-600' : 'text-red-600'}`}
                  >
                    {preview.canArchive ? 'Yes' : 'No'}
                  </span>
                </div>
                {preview.reason && (
                  <Alert variant="destructive" className="mt-2">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{preview.reason}</AlertDescription>
                  </Alert>
                )}
              </div>
            ) : null}
          </div>
        )}

        {/* Dry Run Option */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id="dry-run"
            checked={dryRun}
            onCheckedChange={checked => setDryRun(checked === true)}
          />
          <Label htmlFor="dry-run" className="text-sm font-normal cursor-pointer">
            Dry run (preview only, no data will be deleted)
          </Label>
        </div>

        {/* Warning for non-dry-run */}
        {!dryRun && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>Warning:</strong> Disabling dry run will permanently archive and delete audit
              logs from the database. This action cannot be undone.
            </AlertDescription>
          </Alert>
        )}

        {/* Submit Button */}
        <Button onClick={handleSubmit} disabled={!canSubmit} className="w-full sm:w-auto">
          {isTriggering ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {dryRun ? 'Running preview...' : 'Archiving...'}
            </>
          ) : (
            <>
              <Archive className="mr-2 h-4 w-4" />
              {dryRun ? 'Preview Archive' : 'Archive Now'}
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
